import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createFolder,
  deleteModel,
  dispatchModelsChanged,
  fetchModelSections,
  renameModel,
  uploadModel,
} from '../../services/modelsApi';
import GlbPreviewPanel from './GlbPreviewPanel';

function formatBytes(n) {
  if (n == null || n === '') return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(mtime) {
  if (!mtime) return '';
  try {
    return new Date(mtime * 1000).toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

const MODEL_EXT_RE = /\.(glb|zip)$/i;

function modelExt(name, fallback = 'glb') {
  const m = String(name || '').match(MODEL_EXT_RE);
  return m ? m[1].toLowerCase() : fallback;
}

function stripModelExt(name) {
  return String(name || '').replace(MODEL_EXT_RE, '');
}

function ensureModelExt(name, ext = 'glb') {
  const e = `.${String(ext || 'glb').replace(/^\./, '')}`;
  const base = stripModelExt(name).trim();
  return base ? `${base}${e}` : '';
}

function isModelFileName(name) {
  return MODEL_EXT_RE.test(String(name || ''));
}

function isGlbFileName(name) {
  return /\.glb$/i.test(String(name || ''));
}

async function downloadModelFile(item) {
  if (!item?.url) return;
  const fileName = item.name || 'model.glb';
  try {
    const res = await fetch(item.url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  } catch {
    const a = document.createElement('a');
    a.href = item.url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

function FolderIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
      <path
        fill="#eab308"
        fillOpacity="0.85"
        d="M2 4a1.5 1.5 0 011.5-1.5h3l1 1H13.5A1.5 1.5 0 0115 5v7a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 011 12V4z"
      />
    </svg>
  );
}

function FileIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
      <path fill="#3f3f46" stroke="#a16207" strokeWidth="0.6" d="M3.5 1.5h6l3 3V14.5h-9z" />
      <path fill="#52525b" d="M9.5 1.5v3h3" />
      <text x="8" y="11.5" textAnchor="middle" fontSize="3.2" fill="#eab308" fontFamily="DM Sans, sans-serif">
        GLB
      </text>
    </svg>
  );
}

const toolBtn =
  'inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] rounded-sm border border-transparent text-zinc-400 hover:text-yellow-400 hover:border-yellow-600/30 hover:bg-yellow-500/5 disabled:opacity-35 disabled:hover:text-zinc-400 disabled:hover:border-transparent disabled:hover:bg-transparent transition-colors';


const FileManagement = ({ onBack, userEmail }) => {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentFolder, setCurrentFolder] = useState('models');
  const [selectedPath, setSelectedPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [renamingPath, setRenamingPath] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('New folder');
  const [expanded, setExpanded] = useState(() => new Set(['models']));
  const [previewItem, setPreviewItem] = useState(null);
  const fileInputRef = useRef(null);
  const renameInputRef = useRef(null);
  const newFolderInputRef = useRef(null);
  const shellRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchModelSections();
      setTree(data.tree);
    } catch (e) {
      setError(e.message || 'Failed to load models folder. Check MODELS_ROOT on the Azure VM.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (renamingPath && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingPath]);

  useEffect(() => {
    if (newFolderOpen && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
      newFolderInputRef.current.select();
    }
  }, [newFolderOpen]);

  useEffect(() => {
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  const folderNode = useMemo(() => {
    if (!tree) return null;
    if (currentFolder === 'models' || currentFolder === tree.path) return tree;
    const parts = currentFolder.replace(/^models\/?/, '').split('/').filter(Boolean);
    let node = tree;
    for (const p of parts) {
      const next = (node.children || []).find((c) => c.type === 'folder' && c.name === p);
      if (!next) return node;
      node = next;
    }
    return node;
  }, [tree, currentFolder]);

  const items = useMemo(() => {
    const children = [...(folderNode?.children || [])];
    children.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
    return children;
  }, [folderNode]);

  const breadcrumbs = useMemo(() => {
    const parts = currentFolder.replace(/^models\/?/, '').split('/').filter(Boolean);
    const crumbs = [{ label: 'models', path: 'models' }];
    let acc = 'models';
    parts.forEach((p) => {
      acc = `${acc}/${p}`;
      crumbs.push({ label: p, path: acc });
    });
    return crumbs;
  }, [currentFolder]);

  const parentFolder = useMemo(() => {
    if (currentFolder === 'models') return null;
    const parts = currentFolder.split('/').filter(Boolean);
    parts.pop();
    return parts.length ? parts.join('/') : 'models';
  }, [currentFolder]);

  const selectedItem = useMemo(
    () => items.find((i) => i.path === selectedPath) || null,
    [items, selectedPath]
  );

  const existingNames = useMemo(() => {
    const set = new Set();
    items.forEach((i) => {
      if (i.type === 'file') set.add(stripModelExt(i.name).toLowerCase());
    });
    return set;
  }, [items]);

  const navigateTo = (path) => {
    setCurrentFolder(path);
    setSelectedPath(null);
    setRenamingPath(null);
    setContextMenu(null);
    setExpanded((prev) => {
      const next = new Set(prev);
      const parts = path.replace(/^models\/?/, '').split('/').filter(Boolean);
      let acc = 'models';
      next.add(acc);
      parts.forEach((p) => {
        acc = `${acc}/${p}`;
        next.add(acc);
      });
      return next;
    });
  };

  const openPreview = (item) => {
    if (!item || item.type !== 'file' || !isGlbFileName(item.name) || !item.url) return;
    setSelectedPath(item.path);
    setPreviewItem(item);
    setContextMenu(null);
  };

  const startRename = (item) => {
    if (!item || item.type !== 'file') return;
    setRenamingPath(item.path);
    setRenameValue(stripModelExt(item.name));
    setContextMenu(null);
  };

  const commitRename = async () => {
    if (!renamingPath) return;
    const item = items.find((i) => i.path === renamingPath);
    if (!item || item.type !== 'file') {
      setRenamingPath(null);
      return;
    }
    const nextName = ensureModelExt(renameValue, modelExt(item.name));
    if (!nextName) {
      setError('Enter a valid file name');
      return;
    }
    const folder = renamingPath.replace(/\/[^/]+$/, '');
    const toPath = `${folder}/${nextName}`;
    if (toPath === item.path || stripModelExt(nextName).toLowerCase() === stripModelExt(item.name).toLowerCase()) {
      setRenamingPath(null);
      return;
    }
    try {
      setError('');
      const result = await renameModel(item.path, toPath);
      dispatchModelsChanged({ from: item.path, path: result.path, url: result.url, mtime: result.mtime, size: result.size });
      setRenamingPath(null);
      setSelectedPath(result.path);
      if (previewItem?.path === item.path) {
        setPreviewItem({
          ...previewItem,
          name: nextName,
          path: result.path,
          url: result.url || previewItem.url,
          mtime: result.mtime ?? previewItem.mtime,
        });
      }
      await load();
    } catch (e) {
      setError(e.message || 'Rename failed');
      setRenamingPath(null);
    }
  };

  const handleDelete = async (item) => {
    const target = item || selectedItem;
    if (!target || target.type !== 'file') return;
    if (!window.confirm(`Are you sure you want to permanently delete '${target.name}'?`)) return;
    try {
      await deleteModel(target.path);
      dispatchModelsChanged({ path: target.path, deleted: true });
      setSelectedPath(null);
      if (previewItem?.path === target.path) setPreviewItem(null);
      await load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  };

  const openNewFolder = () => {
    let base = 'New folder';
    let name = base;
    let n = 2;
    const folderNames = new Set(
      items.filter((i) => i.type === 'folder').map((i) => i.name.toLowerCase())
    );
    while (folderNames.has(name.toLowerCase())) {
      name = `${base} (${n})`;
      n += 1;
    }
    setNewFolderName(name);
    setNewFolderOpen(true);
    setContextMenu(null);
  };

  const commitNewFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      setError('Enter a folder name');
      return;
    }
    try {
      setError('');
      const result = await createFolder(currentFolder, name);
      dispatchModelsChanged({ path: result.path, folder: true });
      setNewFolderOpen(false);
      setSelectedPath(result.path);
      setExpanded((prev) => new Set(prev).add(currentFolder));
      await load();
    } catch (e) {
      setError(e.message || 'Failed to create folder');
    }
  };

  const uploadOne = async (file, displayName, replace = false) => {
    const folderRel = currentFolder.replace(/^models\/?/, '');
    const result = await uploadModel({
      file,
      folder: folderRel,
      displayName,
      replace,
      onProgress: setProgress,
    });
    dispatchModelsChanged({
      path: result.path,
      url: result.url,
      mtime: result.mtime,
      size: result.size,
    });
    if (previewItem?.path === result.path) {
      setPreviewItem((prev) =>
        prev && prev.path === result.path
          ? { ...prev, url: result.url, mtime: result.mtime, size: result.size ?? prev.size }
          : prev
      );
    }
    return result;
  };

  const askConflict = (file, displayName) =>
    new Promise((resolve) => {
      setConflict({
        file,
        displayName,
        resolve,
      });
    });

  const handleUploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => isModelFileName(f.name));
    if (!files.length) {
      setError('Please select .glb or .zip files only');
      return;
    }
    setUploading(true);
    setError('');
    try {
      for (const file of files) {
        let displayName = stripModelExt(file.name);
        const existsLocal = existingNames.has(displayName.toLowerCase());
        let replace = false;

        if (existsLocal) {
          const choice = await askConflict(file, displayName);
          if (choice?.action === 'cancel' || !choice) continue;
          if (choice.action === 'replace') {
            replace = true;
          } else if (choice.action === 'rename') {
            displayName = stripModelExt(choice.name);
            if (!displayName) continue;
            if (existingNames.has(displayName.toLowerCase())) {
              const again = await askConflict(file, displayName);
              if (again?.action === 'replace') replace = true;
              else if (again?.action === 'rename') displayName = stripModelExt(again.name);
              else continue;
            }
          }
        }
        try {
          await uploadOne(file, displayName, replace);
          existingNames.add(displayName.toLowerCase());
        } catch (e) {
          if (e.conflict) {
            const choice = await askConflict(file, displayName);
            if (choice?.action === 'replace') {
              await uploadOne(file, displayName, true);
              existingNames.add(displayName.toLowerCase());
            } else if (choice?.action === 'rename') {
              await uploadOne(file, stripModelExt(choice.name), false);
            }
          } else {
            throw e;
          }
        }
      }
      await load();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      setConflict(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onKeyDown = (e) => {
    if (renamingPath) {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitRename();
      } else if (e.key === 'Escape') {
        setRenamingPath(null);
      }
      return;
    }
    if (e.key === 'F2' && selectedItem?.type === 'file') {
      e.preventDefault();
      startRename(selectedItem);
    } else if ((e.key === 'Delete' || e.key === 'Del') && selectedItem?.type === 'file') {
      e.preventDefault();
      handleDelete(selectedItem);
    } else if (e.key === 'Backspace' && parentFolder && e.target === shellRef.current) {
      e.preventDefault();
      navigateTo(parentFolder);
    }
  };

  const renderTreeNode = (node, depth = 0) => {
    if (!node || node.type === 'file') return null;
    const isOpen = expanded.has(node.path);
    const isActive = currentFolder === node.path;
    const folders = (node.children || []).filter((c) => c.type === 'folder');
    return (
      <div key={node.path}>
        <button
          type="button"
          className={`w-full flex items-center gap-1 px-1 py-1 text-left text-[11px] rounded-sm transition-colors ${
            isActive
              ? 'bg-yellow-500/15 text-yellow-300 border border-yellow-600/30'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/80 border border-transparent'
          }`}
          style={{ paddingLeft: 6 + depth * 12 }}
          onClick={() => navigateTo(node.path)}
        >
          <span
            className="w-3 shrink-0 text-[10px] text-yellow-600/70 select-none"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => {
                const next = new Set(prev);
                if (next.has(node.path)) next.delete(node.path);
                else next.add(node.path);
                return next;
              });
            }}
          >
            {folders.length ? (isOpen ? '▾' : '▸') : '·'}
          </span>
          <FolderIcon />
          <span className="truncate">{node.name}</span>
        </button>
        {isOpen && folders.map((f) => renderTreeNode(f, depth + 1))}
      </div>
    );
  };

  return (
    <div
      ref={shellRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-[4000] flex flex-col bg-black text-zinc-100 outline-none"
      style={{ fontFamily: 'var(--mdv-font)', paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <header className="min-h-9 h-12 sm:h-9 bg-black/95 border-b border-yellow-600/25 flex items-center justify-between px-3 shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <FolderIcon className="w-4 h-4" />
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-yellow-400 truncate"
            style={{ fontFamily: 'var(--mdv-display)' }}
          >
            File Management
          </span>
          <span className="text-[10px] text-zinc-600 hidden sm:inline">models</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {userEmail && (
            <span className="text-[10px] text-zinc-500 truncate max-w-[180px]">{userEmail}</span>
          )}
          <button
            type="button"
            onClick={onBack}
            className="text-[10px] font-semibold uppercase tracking-wider min-h-[36px] px-3 py-1.5 rounded-sm border border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10"
          >
            Close
          </button>
        </div>
      </header>

      <div className="bg-zinc-950/90 border-b border-yellow-600/15 px-2 py-1.5 flex flex-wrap items-center gap-0.5 shrink-0">
        <button type="button" disabled={!parentFolder} onClick={() => parentFolder && navigateTo(parentFolder)} className={toolBtn} title="Up">
          ↑ Up
        </button>
        <button type="button" onClick={load} className={toolBtn} title="Refresh">
          ↻ Refresh
        </button>
        <div className="w-px h-4 bg-yellow-600/20 mx-1" />
        <button type="button" onClick={openNewFolder} className={toolBtn} title="New folder">
          New folder
        </button>
        <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} className={toolBtn}>
          Upload
        </button>
        <button
          type="button"
          disabled={!selectedItem || selectedItem.type !== 'file' || !isGlbFileName(selectedItem.name)}
          onClick={() => openPreview(selectedItem)}
          className={toolBtn}
          title="Preview GLB"
        >
          Preview
        </button>
        <button
          type="button"
          disabled={!selectedItem || selectedItem.type !== 'file' || !isGlbFileName(selectedItem.name)}
          onClick={() => selectedItem && downloadModelFile(selectedItem)}
          className={toolBtn}
          title="Download GLB"
        >
          Download
        </button>
        <button
          type="button"
          disabled={selectedItem?.type !== 'file'}
          onClick={() => startRename(selectedItem)}
          className={toolBtn}
          title="Rename (F2)"
        >
          Rename
        </button>
        <button
          type="button"
          disabled={selectedItem?.type !== 'file'}
          onClick={() => handleDelete(selectedItem)}
          className={`${toolBtn} hover:text-red-400`}
          title="Delete"
        >
          Delete
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".glb,.zip,model/gltf-binary,application/zip"
          className="hidden"
          multiple
          disabled={uploading}
          onChange={(e) => handleUploadFiles(e.target.files)}
        />
      </div>

      <div className="bg-black border-b border-yellow-600/15 px-2 py-1.5 flex items-center gap-2 shrink-0">
        <span className="text-[9px] uppercase tracking-[0.14em] text-yellow-600/70 shrink-0">Path</span>
        <div className="flex-1 flex items-center gap-0.5 border border-yellow-600/25 bg-zinc-950/80 px-1.5 py-1 text-[11px] min-h-[26px] overflow-x-auto rounded-sm">
          {breadcrumbs.map((c, i) => (
            <React.Fragment key={c.path}>
              {i > 0 && <span className="text-zinc-600 px-0.5">›</span>}
              <button
                type="button"
                className="hover:bg-yellow-500/10 hover:text-yellow-400 px-1 rounded-sm shrink-0 text-zinc-300 transition-colors"
                onClick={() => navigateTo(c.path)}
              >
                {c.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-950/40 border-b border-red-500/30 text-red-300 text-[11px] px-3 py-1.5 shrink-0">
          {error}
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        <aside className="w-48 shrink-0 border-r border-yellow-600/15 bg-zinc-950/60 overflow-auto py-1.5 px-1 hidden sm:block">
          <p className="px-1.5 py-1 text-[9px] uppercase tracking-[0.16em] text-yellow-600/70">Folders</p>
          {tree ? renderTreeNode(tree) : <p className="px-2 text-[11px] text-zinc-600">…</p>}
        </aside>

        <main
          className={`flex-1 min-w-0 bg-zinc-950 overflow-auto relative ${
            dragOver ? 'ring-1 ring-inset ring-yellow-500/50' : ''
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleUploadFiles(e.dataTransfer.files);
          }}
          onClick={() => {
            if (!renamingPath) setSelectedPath(null);
          }}
        >
          {loading ? (
            <p className="p-4 text-[11px] text-zinc-500">Loading…</p>
          ) : (
            <table className="w-full text-[11px] border-collapse">
              <thead className="sticky top-0 bg-black/95 border-b border-yellow-600/20 z-10">
                <tr className="text-left text-yellow-600/80">
                  <th className="font-semibold uppercase tracking-[0.1em] text-[9px] px-2 py-2 w-[45%]">Name</th>
                  <th className="font-semibold uppercase tracking-[0.1em] text-[9px] px-2 py-2 w-[25%]">Modified</th>
                  <th className="font-semibold uppercase tracking-[0.1em] text-[9px] px-2 py-2 w-[15%]">Type</th>
                  <th className="font-semibold uppercase tracking-[0.1em] text-[9px] px-2 py-2 w-[15%] text-right">Size</th>
                </tr>
              </thead>
              <tbody>
                {items.map((child) => {
                  const selected = selectedPath === child.path;
                  const renaming = renamingPath === child.path;
                  return (
                    <tr
                      key={child.path}
                      className={`border-b border-zinc-900 cursor-default transition-colors ${
                        selected
                          ? 'bg-yellow-500/15 text-yellow-100'
                          : 'text-zinc-300 hover:bg-yellow-500/5'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPath(child.path);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        if (child.type === 'folder') navigateTo(child.path);
                        else startRename(child);
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedPath(child.path);
                        setContextMenu({ x: e.clientX, y: e.clientY, item: child });
                      }}
                    >
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {child.type === 'folder' ? <FolderIcon /> : <FileIcon />}
                          {renaming ? (
                            <input
                              ref={renameInputRef}
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onBlur={commitRename}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitRename();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setRenamingPath(null);
                                }
                                e.stopPropagation();
                              }}
                              className="flex-1 min-w-0 border border-yellow-600/50 bg-black px-1.5 py-0.5 text-[11px] text-zinc-100 outline-none rounded-sm"
                            />
                          ) : (
                            <span className="truncate flex-1 min-w-0">{child.name}</span>
                          )}
                          {child.type === 'file' && isGlbFileName(child.name) && !renaming && (
                            <div
                              className="flex items-center gap-0.5 shrink-0 ml-auto"
                              onDoubleClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                title="Preview"
                                className={`${toolBtn} py-0.5 px-1.5 ${
                                  previewItem?.path === child.path ? 'text-yellow-400 border-yellow-600/30 bg-yellow-500/10' : ''
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPreview(child);
                                }}
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                title="Download"
                                className={`${toolBtn} py-0.5 px-1.5`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadModelFile(child);
                                }}
                              >
                                Download
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">
                        {child.type === 'file' ? formatDate(child.mtime) : ''}
                      </td>
                      <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">
                        {child.type === 'folder' ? 'Folder' : modelExt(child.name, '').toUpperCase() || 'File'}
                      </td>
                      <td className="px-2 py-1.5 text-zinc-500 text-right whitespace-nowrap">
                        {child.type === 'file' ? formatBytes(child.size) : ''}
                      </td>
                    </tr>
                  );
                })}
                {!items.length && (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-zinc-600">
                      Empty folder — drag .glb or .zip files here or use Upload
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {dragOver && (
            <div className="pointer-events-none absolute inset-0 bg-yellow-500/5 flex items-center justify-center">
              <div className="border border-yellow-600/50 bg-black/90 px-4 py-2 text-[11px] text-yellow-400 uppercase tracking-wider rounded-sm">
                Drop .glb or .zip files
              </div>
            </div>
          )}
        </main>
        {previewItem && (
          <GlbPreviewPanel item={previewItem} onClose={() => setPreviewItem(null)} />
        )}
      </div>

      <footer className="h-7 bg-black border-t border-yellow-600/20 px-3 flex items-center justify-between text-[10px] text-zinc-500 shrink-0">
        <span>
          {items.length} item{items.length === 1 ? '' : 's'}
          {selectedItem ? ` · ${selectedItem.name}` : ''}
        </span>
        {uploading ? (
          <span className="text-yellow-500">Uploading… {progress}%</span>
        ) : (
          <span className="text-zinc-600 uppercase tracking-wider">Ready</span>
        )}
      </footer>

      {contextMenu && (
        <ul
          className="fixed z-[4100] min-w-[9rem] bg-zinc-950 border border-yellow-600/35 shadow-2xl py-1 text-[11px] rounded-sm overflow-hidden"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <li>
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-yellow-500/10 hover:text-yellow-400"
              onClick={() => {
                setContextMenu(null);
                openNewFolder();
              }}
            >
              New folder
            </button>
          </li>
          <li className="h-px bg-yellow-600/20 my-1" />
          {contextMenu.item.type === 'folder' ? (
            <li>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-yellow-500/10 hover:text-yellow-400"
                onClick={() => {
                  navigateTo(contextMenu.item.path);
                  setContextMenu(null);
                }}
              >
                Open
              </button>
            </li>
          ) : (
            <>
              <li>
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-yellow-500/10 hover:text-yellow-400 disabled:opacity-40"
                  disabled={!isGlbFileName(contextMenu.item.name)}
                  onClick={() => openPreview(contextMenu.item)}
                >
                  Preview
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-yellow-500/10 hover:text-yellow-400 disabled:opacity-40"
                  disabled={!isGlbFileName(contextMenu.item.name)}
                  onClick={() => {
                    downloadModelFile(contextMenu.item);
                    setContextMenu(null);
                  }}
                >
                  Download
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-yellow-500/10 hover:text-yellow-400"
                  onClick={() => startRename(contextMenu.item)}
                >
                  Rename
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-red-400 hover:bg-red-950/40"
                  onClick={() => {
                    setContextMenu(null);
                    handleDelete(contextMenu.item);
                  }}
                >
                  Delete
                </button>
              </li>
            </>
          )}
        </ul>
      )}

      {newFolderOpen && (
        <div className="fixed inset-0 z-[4200] bg-black/70 flex items-center justify-center p-4 backdrop-blur-[1px]">
          <div className="bg-zinc-950 border border-yellow-600/40 shadow-[0_20px_50px_rgba(0,0,0,0.55)] w-full max-w-sm rounded-md overflow-hidden">
            <div className="border-b border-yellow-600/25 px-3 py-2">
              <p className="text-[9px] uppercase tracking-[0.16em] text-yellow-500/85">New folder</p>
            </div>
            <div className="p-4 space-y-3 text-[12px]">
              <p className="text-zinc-400 text-[11px]">
                In <span className="text-zinc-200">{breadcrumbs.map((c) => c.label).join(' › ')}</span>
              </p>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500">
                Name
                <input
                  ref={newFolderInputRef}
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitNewFolder();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setNewFolderOpen(false);
                    }
                  }}
                  className="mt-1 w-full border border-yellow-600/30 bg-black px-2 py-1.5 text-[12px] text-zinc-100 rounded-sm outline-none focus:border-yellow-500/50"
                />
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-zinc-700 text-zinc-300 rounded-sm hover:border-zinc-500"
                  onClick={() => setNewFolderOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider bg-yellow-500 text-black font-semibold rounded-sm hover:bg-yellow-400"
                  onClick={commitNewFolder}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {conflict && (
        <div className="fixed inset-0 z-[4200] bg-black/70 flex items-center justify-center p-4 backdrop-blur-[1px]">
          <div className="bg-zinc-950 border border-yellow-600/40 shadow-[0_20px_50px_rgba(0,0,0,0.55)] w-full max-w-md rounded-md overflow-hidden">
            <div className="border-b border-yellow-600/25 px-3 py-2">
              <p className="text-[9px] uppercase tracking-[0.16em] text-yellow-500/85">File exists</p>
            </div>
            <div className="p-4 space-y-3 text-[12px]">
              <p className="text-zinc-300">
                Already have{' '}
                <strong className="text-yellow-400 font-medium">{ensureModelExt(conflict.displayName, modelExt(conflict.file?.name))}</strong>
              </p>
              <p className="text-zinc-500 text-[11px]">Source: {conflict.file.name}</p>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500">
                New name
                <input
                  type="text"
                  defaultValue={`${stripModelExt(conflict.displayName)} (2)`}
                  id="fm-conflict-rename"
                  className="mt-1 w-full border border-yellow-600/30 bg-black px-2 py-1.5 text-[12px] text-zinc-100 rounded-sm outline-none focus:border-yellow-500/50"
                />
              </label>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-zinc-700 text-zinc-300 rounded-sm hover:border-zinc-500"
                  onClick={() => {
                    const resolve = conflict.resolve;
                    setConflict(null);
                    resolve({ action: 'cancel' });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-yellow-600/40 text-yellow-400 rounded-sm hover:bg-yellow-500/10"
                  onClick={() => {
                    const input = document.getElementById('fm-conflict-rename');
                    const name = stripModelExt(input?.value || '');
                    const resolve = conflict.resolve;
                    setConflict(null);
                    resolve({ action: 'rename', name: name || `${conflict.displayName} (2)` });
                  }}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider bg-yellow-500 text-black font-semibold rounded-sm hover:bg-yellow-400"
                  onClick={() => {
                    const resolve = conflict.resolve;
                    setConflict(null);
                    resolve({ action: 'replace' });
                  }}
                >
                  Replace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManagement;
